import { useEffect, useState, useCallback, useMemo } from "react";
import Order from "./Order";
import DetailsModal from "./DetailsModal";
import "../Css/Complete.css";
import Logger from "./Logger";
import PaginationControls from "./Pagination";
import { Typography, CircularProgress, Dialog, DialogTitle, DialogContent, IconButton, Button, } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { FaListUl } from "react-icons/fa";
import { connectSocket } from "../utils/connectSocket";
import { getLoggerBaseURL } from "../utils/getLoggerBaseURL";

function BillToCollect() {
  const userEmail = sessionStorage.getItem("userEmail");
  const [selectedOption, setSelectedOption] = useState("OrderNo");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [orders, setOrders] = useState([]);
  const [showDModal, setShowDModal] = useState(false);
  const [selectedOrderNo, setSelectedOrderNo] = useState(null);
  const [images, setImages] = useState([]);
  const [signature, setSignature] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const [loading, setLoading] = useState(true);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [openSignatureModal, setOpenSignatureModal] = useState(false);
  const navigate = useNavigate();
  const [totalOrders, setTotalOrders] = useState(0);

  const logger = useMemo(() => new Logger(getLoggerBaseURL()), []);

  useEffect(() => {
    const socket = connectSocket();

    socket.on("success", (order) => {
      setOrders((prevOrders) => {
        const orderIndex = prevOrders.findIndex((o) => o._id === order._id);
        if (orderIndex !== -1) {
          return prevOrders.map((o) =>
            o._id === order._id
              ? { ...order, driverName: order.driverName || o.driverName }
              : o
          );
        } else {
          return [...prevOrders, order];
        }
      });
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const fetchOrders = useCallback(async (page = 1) => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page,
        limit: 50,
        startDate: startDate || "",
        endDate: endDate || "",
        selectedOption,
        keyword: searchQuery || "",
      });

      const res = await fetch(`/api/ordera/billToCollect/${userEmail}?${params.toString()}`);

      const json = await res.json();

      setOrders(json.data);
      setTotalOrders(json.total);
      setCurrentPage(page);
      setLoading(false);
    } catch (error) {
      logger.log(`Error fetching orders for email ${userEmail}: ${error.message}`, "ERROR");
      setOrders([]); // Set orders to an empty array in case of error
    } finally {
      setLoading(false); // Ensure loading state is updated even on error
    }
  }, [startDate, endDate, selectedOption, searchQuery, userEmail, logger]);

  useEffect(() => {
    fetchOrders(1);
  }, [fetchOrders]);

  const handleOpenModal = async (orderNo) => {
    const fileName = `${orderNo}.jpg`;
    const encodedFileName = encodeURIComponent(fileName);

    setSelectedOrderNo(orderNo);
    setOpenImageModal(true);

    try {
      const response = await fetch(`/api/ordera/image/${encodedFileName}`);
      const data = await response.json();

      if (data.files && data.files.length > 0) {
        setImages(data.files.map((f) => f.url)); // only keep the URLs
        setCurrentImageIndex(0);
      } else {
        setImages([]);
      }
    } catch (error) {
      logger.log(
        `Error fetching images for order ${orderNo}: ${error.message}`,
        "ERROR"
      );
      setImages([]);
    }
  };

  const handleViewSignature = async (orderNo) => {
    const fileName = `${orderNo}.jpg`;
    const encodedFileName = encodeURIComponent(fileName);
    setSelectedOrderNo(encodedFileName);
    setOpenSignatureModal(true);
    try {
      const sigRes = await fetch(`/api/ordera/signature/${encodedFileName}`);
      if (sigRes.ok) {
        const blob = await sigRes.blob();
        const url = URL.createObjectURL(blob);
        setSignature(url);
      } else {
        setSignature(null);
      }
    } catch (error) {
      logger.log(
        `Error fetching signature for order ${orderNo}: ${error.message}`,
        "ERROR"
      );
      setSignature(null);
    }
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);
    setImages([]);
    setCurrentImageIndex(0);
  };

  const handleCloseSignatureModal = () => {
    setOpenSignatureModal(false);
    setSignature(null);
  };

  const handleCloseModal = () => {
    setShowDModal(false);
    setSelectedOrder(null);
  };

  const handleViewHistory = (orderId) => {
    navigate(`/timeline/${orderId}`);
  };

  return (
    <div>
      <Order
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />
      <div>
        {loading ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
            }}
          >
            <CircularProgress />
            <Typography variant="h6" style={{ marginTop: "20px" }}>
              Fetching data...
            </Typography>
          </div>
        ) : (
          <table className="cTable">
            <thead>
              <tr>
                <th className="thC">
                  <strong>Order No.</strong>
                </th>
                <th className="thC">
                  <strong>Company Name</strong>
                </th>
                <th className="thC">
                  <strong>Company Address</strong>
                </th>
                <th className="thC">
                  <strong>Distance</strong>
                </th>
                <th className="thC">
                  <strong>Date For Collection</strong>
                </th>
                <th className="thC">
                  <strong>Total Amount</strong>
                </th>
                <th className="thC">
                  <strong>Driver</strong>
                </th>
                <th className="thC">
                  <strong>Mode of Payment 1</strong>
                </th>
                <th className="thC">
                  <strong>Amount 1</strong>
                </th>
                <th className="thC">
                  <strong>Mode of Payment 2</strong>
                </th>
                <th className="thC">
                  <strong>Amount 2</strong>
                </th>
                <th className="thC">
                  <strong>Mode of Payment 3</strong>
                </th>
                <th className="thC">
                  <strong>Amount 3</strong>
                </th>
                <th className="thC">
                  <strong>Comments</strong>
                </th>
                <th className="thC">
                  <strong>Proof of Delivery</strong>
                </th>
                <th className="thC">
                  <strong>Signature</strong>
                </th>
                <th className="thC">
                  <strong>History</strong>
                </th>
              </tr>
            </thead>
            <tbody>
              {orders?.length === 0 ? (
                <tr>
                  <td className="noOrders" colSpan="15">
                    <div className="center-content">
                      <div className="icon-wrapper">
                        <FaListUl className="icon" />
                      </div>
                      <span className="noOrdersMsg">
                        No Orders found
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  return (
                    <tr key={order._id}>
                      <td className="tdC">
                        <button className="detailsD"                        >
                          <strong>{order.Order_No || "N/A"}</strong>
                        </button>
                      </td>
                      <td className="tdC">{order.Rec_Name || "N/A"}</td>
                      <td className="tdC">{order.Rec_Add || "N/A"}</td>
                      <td className="tdC">{order.distance || "N/A"}</td>
                      <td>
                        {new Date(
                          order.isBillToCollect.date
                        ).toLocaleDateString("en-GB")}
                      </td>

                      <td className="tdC">{`₹ ${order.amount}` || "N/A"}</td>
                      <td className="tdC">{order.driverName || "N/A"}</td>
                      <td className="tdC">
                        {order.mop === null ||
                          order.mop === undefined ||
                          order.mop === ""
                          ? "N/A"
                          : order.mop}
                      </td>
                      <td className="tdC">{`₹ ${order.amount1}` || "N/A"}</td>
                      <td className="tdC">
                        {order.mop2 === null ||
                          order.mop2 === undefined ||
                          order.mop2 === ""
                          ? "N/A"
                          : order.mop2}
                      </td>
                      <td className="tdC">{`₹ ${order.amount2}` || "N/A"}</td>
                      <td className="tdC">
                        {order?.mop3 ? order.mop3 : "N/A"}
                      </td>
                      <td className="tdC">
                        {order?.amount3 !== undefined && order.amount3 !== null
                          ? `₹ ${order.amount3}`
                          : "N/A"}
                      </td>
                      <td className="tdC">{order.comments || "N/A"}</td>
                      <td className="tdC">
                        <button
                          className="detailsDD"
                          onClick={() => handleOpenModal(order.Order_No)}
                        >
                          View Images
                        </button>
                      </td>
                      <td className="tdC">
                        <button
                          className="detailsDD"
                          onClick={() => handleViewSignature(order.Order_No)}
                        >
                          View Signature
                        </button>
                      </td>
                      <td className="tdC">
                        <button
                          className="detailsDD"
                          onClick={() => handleViewHistory(order._id)}
                        >
                          View History
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: "1rem",
          marginRight: "30px",
        }}
      >
        <PaginationControls
          totalItems={totalOrders}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Details Modal */}
      {showDModal && selectedOrder && (
        <DetailsModal
          showDModal={showDModal}
          onClose={handleCloseModal}
          selectedOrder={selectedOrder}
        />
      )}

      {/* Image Modal */}
      <Dialog
        open={openImageModal}
        onClose={handleCloseImageModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{ textAlign: "center", position: "relative", fontWeight: "bold" }}
        >
          Proof of Delivery
          <IconButton
            aria-label="close"
            onClick={handleCloseImageModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
          }}
        >
          {images.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <img
                src={images[currentImageIndex]}
                alt={`Order ${selectedOrderNo}`}
                loading="lazy"
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                  display: "block",
                }}
              />
              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <Button
                  onClick={() =>
                    setCurrentImageIndex((i) => Math.max(0, i - 1))
                  }
                  disabled={currentImageIndex === 0}
                >
                  Prev
                </Button>
                <Typography>
                  {currentImageIndex + 1} / {images.length}
                </Typography>
                <Button
                  onClick={() =>
                    setCurrentImageIndex((i) =>
                      Math.min(images.length - 1, i + 1)
                    )
                  }
                  disabled={currentImageIndex === images.length - 1}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : (
            <Typography sx={{ textAlign: "center" }}>
              No image found for this order
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Signature Modal */}
      <Dialog
        open={openSignatureModal}
        onClose={handleCloseSignatureModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ textAlign: "center", position: "relative", fontWeight: "bold" }}
        >
          Signature
          <IconButton
            aria-label="close"
            onClick={handleCloseSignatureModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
          }}
        >
          {signature ? (
            <img
              src={signature}
              alt="Signature"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                display: "block",
                margin: "0 auto",
              }}
            />
          ) : (
            <Typography sx={{ textAlign: "center" }}>
              No signature found for this order
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default BillToCollect;
